/**
 * Watchlist Page
 *
 * Manage carriers being monitored for alerts.
 * Add, remove, and configure alert settings per carrier.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/shared/Navbar';
import { watchlistApi } from '../services/realtime-alert.service';
import type { WatchlistEntry } from '../services/realtime-alert.service';
import { carrierApi } from '../services/api';
import type { SearchResult } from '../types';

const WatchlistPage: React.FC = () => {
  const navigate = useNavigate();

  const [watchlist, setWatchlist] = useState<WatchlistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Modal state
  const [selectedCarrier, setSelectedCarrier] = useState<WatchlistEntry | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Load watchlist
  const loadWatchlist = useCallback(async () => {
    try {
      const { watchlist: data } = await watchlistApi.getWatchlist();
      setWatchlist(data || []);
    } catch (err) {
      setError('Failed to load watchlist');
      console.error('Error loading watchlist:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWatchlist();
  }, [loadWatchlist]);

  // Search carriers
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Try searching by DOT number first
      const dotNumber = parseInt(searchQuery);
      if (!isNaN(dotNumber) && dotNumber > 0) {
        try {
          const carrier = await carrierApi.getProfile(dotNumber);
          if (carrier) {
            setSearchResults([{
              dotNumber: carrier.dotNumber,
              legalName: carrier.legalName,
              dbaName: carrier.dbaName,
              phyCity: carrier.phyCity,
              phyState: carrier.phyState,
              statusCode: carrier.statusCode,
              safetyRating: carrier.safetyRating,
              totalPowerUnits: carrier.totalPowerUnits,
            }]);
            return;
          }
        } catch {
          // DOT not found, continue with name search
        }
      }

      // Search by name
      const results = await carrierApi.search(searchQuery, 'name');
      setSearchResults(results.slice(0, 10));
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Add carrier to watchlist
  const handleAddCarrier = async (dotNumber: number) => {
    try {
      await watchlistApi.addToWatchlist(dotNumber);
      await loadWatchlist();
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error adding carrier:', err);
      setError('Failed to add carrier to watchlist');
    }
  };

  // Remove carrier from watchlist
  const handleRemoveCarrier = async (dotNumber: number) => {
    if (!confirm('Are you sure you want to remove this carrier from your watchlist?')) {
      return;
    }

    try {
      await watchlistApi.removeFromWatchlist(dotNumber);
      setWatchlist(prev => prev.filter(w => w.dotNumber !== dotNumber));
    } catch (err) {
      console.error('Error removing carrier:', err);
      setError('Failed to remove carrier from watchlist');
    }
  };

  // Update carrier settings
  const handleUpdateSettings = async (dotNumber: number, settings: WatchlistEntry['alertSettings']) => {
    try {
      await watchlistApi.updateWatchlistSettings(dotNumber, { alertSettings: settings });
      setWatchlist(prev =>
        prev.map(w =>
          w.dotNumber === dotNumber ? { ...w, alertSettings: settings } : w
        )
      );
      setShowSettingsModal(false);
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to update settings');
    }
  };

  // Navigate to carrier dashboard
  const handleViewCarrier = (dotNumber: number) => {
    navigate(`/dashboard?dot=${dotNumber}`);
  };

  // Check if carrier is already watched
  const isWatched = (dotNumber: number) => {
    return watchlist.some(w => w.dotNumber === dotNumber);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Carrier Watchlist</h1>
          <p className="text-gray-500 mt-1">
            Monitor carriers and receive real-time alerts for safety events
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Add Carrier Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Carrier to Watchlist</h2>

          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search by DOT number or company name..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 border border-gray-200 rounded-lg divide-y divide-gray-200">
              {searchResults.map(carrier => (
                <div
                  key={carrier.dotNumber}
                  className="p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">{carrier.legalName}</p>
                    <p className="text-sm text-gray-500">
                      DOT {carrier.dotNumber}
                      {carrier.phyCity && ` - ${carrier.phyCity}, ${carrier.phyState}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAddCarrier(carrier.dotNumber)}
                    disabled={isWatched(carrier.dotNumber)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${
                      isWatched(carrier.dotNumber)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {isWatched(carrier.dotNumber) ? 'Already Watching' : 'Add to Watchlist'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Watchlist */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Watched Carriers ({watchlist.length})
            </h2>
          </div>

          {watchlist.length === 0 ? (
            <div className="p-12 text-center">
              <svg
                className="w-16 h-16 mx-auto text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No carriers watched</h3>
              <p className="mt-2 text-sm text-gray-500">
                Add carriers to your watchlist to receive real-time alerts
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {watchlist.map(entry => (
                <div
                  key={entry.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{entry.legalName}</h3>
                        {entry.unreadCount > 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            {entry.unreadCount} new
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        DOT {entry.dotNumber}
                        {entry.location && ` - ${entry.location}`}
                      </p>

                      {/* Stats Row */}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                        {entry.safetyRating && (
                          <span
                            className={`px-2 py-0.5 rounded ${
                              entry.safetyRating === 'Satisfactory'
                                ? 'bg-green-100 text-green-700'
                                : entry.safetyRating === 'Conditional'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {entry.safetyRating}
                          </span>
                        )}
                        {entry.authStatus && (
                          <span
                            className={`px-2 py-0.5 rounded ${
                              entry.authStatus === 'AUTHORIZED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {entry.authStatus}
                          </span>
                        )}
                        <span className="text-gray-500">
                          Added {new Date(entry.addedAt).toLocaleDateString()}
                        </span>
                        <span className="text-gray-500">
                          {entry.alertCount} total alerts
                        </span>
                      </div>

                      {/* Notes */}
                      {entry.notes && (
                        <p className="mt-2 text-sm text-gray-600 italic">
                          "{entry.notes}"
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleViewCarrier(entry.dotNumber)}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        View Profile
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCarrier(entry);
                          setShowSettingsModal(true);
                        }}
                        className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        Settings
                      </button>
                      <button
                        onClick={() => handleRemoveCarrier(entry.dotNumber)}
                        className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && selectedCarrier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Alert Settings for {selectedCarrier.legalName}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {[
                { key: 'violations', label: 'Violation Alerts' },
                { key: 'crashes', label: 'Crash Alerts' },
                { key: 'scoreChanges', label: 'Score Change Alerts' },
                { key: 'networkChanges', label: 'Network Change Alerts' },
                { key: 'shellGame', label: 'Shell Game Alerts' },
                { key: 'contactMatch', label: 'Contact Match Alerts' },
                { key: 'oos', label: 'Out-of-Service Alerts' },
                { key: 'fatal', label: 'Fatal Crash Alerts' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between">
                  <span className="text-gray-700">{label}</span>
                  <input
                    type="checkbox"
                    checked={selectedCarrier.alertSettings[key as keyof typeof selectedCarrier.alertSettings]}
                    onChange={e => {
                      setSelectedCarrier({
                        ...selectedCarrier,
                        alertSettings: {
                          ...selectedCarrier.alertSettings,
                          [key]: e.target.checked,
                        },
                      });
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </label>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleUpdateSettings(selectedCarrier.dotNumber, selectedCarrier.alertSettings)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistPage;
